import React, { useState, useRef } from 'react';
import { LogIn, Lock, User, AlertCircle, X, Phone, KeyRound, ShieldCheck, Building2 } from 'lucide-react';
import { GlowButton } from '../../components/landing/LandingPrimitives';
import PasswordStrengthIndicator from '../../components/profile/PasswordStrengthIndicator';
import { PASSWORD_MIN_LENGTH } from '../../constants/authPolicy';
import { authAPI } from '../../services/api';

export default function LoginForm({
  method = 'phone',
  phone = '',
  onPhoneChange = () => {},
  otp = '',
  onOtpChange = () => {},
  otpSent = false,
  resendCooldown = 0,
  onSendOtp = () => {},
  onMethodToggle = () => {},
  onChangePhone = () => {},
  isRegistering = false,
  regName = '',
  onRegNameChange = () => {},
  regFacilityName = '',
  onRegFacilityNameChange = () => {},
  regPassword = '',
  onRegPasswordChange = () => {},
  regConfirmPassword = '',
  onRegConfirmPasswordChange = () => {},
  onRegister = () => {},
  username,
  onUsernameChange,
  password,
  onPasswordChange,
  error,
  loading,
  onSubmit,
}) {
  const [showForgotModal, setShowForgotModal] = useState(false);

  // Forgot-password modal state
  const [fpStep, setFpStep] = useState(1); // 1=phone, 2=otp, 3=new password
  const [fpPhone, setFpPhone] = useState('');
  const [fpOtp, setFpOtp] = useState('');
  const [fpPassword, setFpPassword] = useState('');
  const [fpConfirm, setFpConfirm] = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [fpError, setFpError] = useState('');
  const [fpSuccess, setFpSuccess] = useState(false);
  const [fpResendCooldown, setFpResendCooldown] = useState(0);
  const cooldownTimer = useRef(null);

  const resetFpModal = () => {
    setFpStep(1); setFpPhone(''); setFpOtp(''); setFpPassword(''); setFpConfirm('');
    setFpLoading(false); setFpError(''); setFpSuccess(false); setFpResendCooldown(0);
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
  };

  const startCooldown = (seconds = 30) => {
    setFpResendCooldown(seconds);
    cooldownTimer.current = setInterval(() => {
      setFpResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownTimer.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleFpSendOtp = async () => {
    setFpError('');
    if (!fpPhone || fpPhone.replace(/\D/g, '').length < 10) {
      return setFpError('Enter a valid 10-digit phone number');
    }
    setFpLoading(true);
    try {
      await authAPI.forgotPassword(fpPhone.replace(/\D/g, ''));
      setFpStep(2);
      startCooldown(30);
    } catch (err) {
      setFpError(err.message);
    } finally {
      setFpLoading(false);
    }
  };

  const handleFpVerifyOtp = async () => {
    setFpError('');
    if (!fpOtp || fpOtp.length < 6) return setFpError('Enter the 6-digit OTP');
    setFpStep(3);
  };

  const handleFpResetPassword = async () => {
    setFpError('');
    if (!fpPassword || fpPassword.length < PASSWORD_MIN_LENGTH) return setFpError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters`);
    if (fpPassword !== fpConfirm) return setFpError('Passwords do not match');
    setFpLoading(true);
    try {
      await authAPI.resetPassword(fpPhone.replace(/\D/g, ''), fpOtp, fpPassword);
      setFpSuccess(true);
    } catch (err) {
      setFpError(err.message);
      setFpStep(2); // go back to OTP step on error
    } finally {
      setFpLoading(false);
    }
  };

  // Verified phone with no account → 2-field owner registration
  // (plan §12 unified entry). Replaces the whole login form.
  if (isRegistering) {
    return (
      <>
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-400/30 rounded-xl">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          </div>
        )}

        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white mb-1">Welcome to Ground Aale!</h2>
          <p className="text-sm text-slate-400">
            Your number <span className="text-white">{phone}</span> is verified.
            Add your details and password to finish setting up your ground.
          </p>
        </div>

        <form onSubmit={onRegister} className="space-y-5">
          <div>
            <label htmlFor="reg-name" className="block text-sm font-medium text-slate-300 mb-2">
              Your name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-slate-500" />
              </div>
              <input
                id="reg-name"
                type="text"
                value={regName}
                onChange={(e) => onRegNameChange(e.target.value)}
                className="landing-input block w-full pl-10 pr-3 py-3"
                placeholder="e.g. Amit Tyagi"
                autoComplete="name"
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label htmlFor="reg-facility" className="block text-sm font-medium text-slate-300 mb-2">
              Facility name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building2 className="h-5 w-5 text-slate-500" />
              </div>
              <input
                id="reg-facility"
                type="text"
                value={regFacilityName}
                onChange={(e) => onRegFacilityNameChange(e.target.value)}
                className="landing-input block w-full pl-10 pr-3 py-3"
                placeholder="e.g. Tyagi Sports Ground"
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label htmlFor="reg-username" className="block text-sm font-medium text-slate-300 mb-2">
              Username
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-slate-500" />
              </div>
              <input
                id="reg-username"
                type="text"
                value={phone}
                readOnly
                className="landing-input block w-full pl-10 pr-3 py-3 opacity-80 cursor-not-allowed"
                disabled
              />
            </div>
            <p className="mt-1 text-xs text-slate-500">Your username is your verified phone number.</p>
          </div>

          <div>
            <label htmlFor="reg-password" className="block text-sm font-medium text-slate-300 mb-2">
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-slate-500" />
              </div>
              <input
                id="reg-password"
                type="password"
                value={regPassword}
                onChange={(e) => onRegPasswordChange(e.target.value)}
                className="landing-input block w-full pl-10 pr-3 py-3"
                placeholder="Create a password"
                autoComplete="new-password"
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label htmlFor="reg-confirm-password" className="block text-sm font-medium text-slate-300 mb-2">
              Confirm password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-slate-500" />
              </div>
              <input
                id="reg-confirm-password"
                type="password"
                value={regConfirmPassword}
                onChange={(e) => onRegConfirmPasswordChange(e.target.value)}
                className="landing-input block w-full pl-10 pr-3 py-3"
                placeholder="Re-enter your password"
                autoComplete="new-password"
                disabled={loading}
              />
            </div>
          </div>

          {(regPassword || regConfirmPassword) && (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <PasswordStrengthIndicator password={regPassword} />
            </div>
          )}

          <GlowButton type="submit" disabled={loading} className="w-full py-3">
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-950" />
                <span>Creating your account...</span>
              </>
            ) : (
              <>
                <LogIn className="h-5 w-5" />
                <span>Create my account</span>
              </>
            )}
          </GlowButton>

          <div className="text-center">
            <button
              type="button"
              onClick={onChangePhone}
              className="text-sm text-slate-400 hover:text-ga-lime transition-colors"
            >
              Use a different phone number
            </button>
          </div>
        </form>
      </>
    );
  }

  return (
    <>
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-400/30 rounded-xl">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-5">
        {method === 'phone' ? (
          <>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="login-phone" className="block text-sm font-medium text-slate-300">
                  Phone number
                </label>
                {otpSent && (
                  <button
                    type="button"
                    onClick={onChangePhone}
                    className="text-xs text-ga-green hover:text-ga-lime transition-colors"
                  >
                    Change number
                  </button>
                )}
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-slate-500" />
                </div>
                <input
                  id="login-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => onPhoneChange(e.target.value)}
                  className="landing-input block w-full pl-10 pr-3 py-3"
                  placeholder="10-digit phone number"
                  autoComplete="tel-national"
                  disabled={loading || otpSent}
                />
              </div>
            </div>

            {otpSent && (
              <div>
                <label htmlFor="login-otp" className="block text-sm font-medium text-slate-300 mb-2">
                  Verification code
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <KeyRound className="h-5 w-5 text-slate-500" />
                  </div>
                  <input
                    id="login-otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => onOtpChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="landing-input block w-full pl-10 pr-3 py-3 tracking-widest"
                    placeholder="6-digit code"
                    autoComplete="one-time-code"
                    disabled={loading}
                  />
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  A code is on its way to your number.
                  <button
                    type="button"
                    onClick={() => { if (resendCooldown === 0 && !loading) onSendOtp(); }}
                    disabled={resendCooldown > 0 || loading}
                    className="ml-2 text-ga-green hover:text-ga-lime disabled:opacity-50 transition-colors"
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                  </button>
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-slate-300 mb-2">
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-slate-500" />
                </div>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => onUsernameChange(e.target.value)}
                  className="landing-input block w-full pl-10 pr-3 py-3"
                  placeholder="Enter your username"
                  autoComplete="username"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-slate-300">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotModal(true)}
                  className="text-xs text-ga-green hover:text-ga-lime transition-colors"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-slate-500" />
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => onPasswordChange(e.target.value)}
                  className="landing-input block w-full pl-10 pr-3 py-3"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={loading}
                />
              </div>
            </div>
          </>
        )}

        {/* Forgot password modal — 3-step OTP flow */}
        {showForgotModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="relative mx-4 w-full max-w-sm rounded-2xl border border-white/10 bg-[#1E293B] p-6 shadow-2xl">
              <button
                type="button"
                onClick={() => { setShowForgotModal(false); resetFpModal(); }}
                className="absolute right-4 top-4 text-slate-500 hover:text-slate-300 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>

              {fpSuccess ? (
                <div className="text-center py-4">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-ga-green/10 mx-auto">
                    <ShieldCheck className="h-6 w-6 text-ga-green" />
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-white">Password Reset!</h3>
                  <p className="text-sm text-slate-400 mb-5">Your password has been updated. You can now sign in with your new password.</p>
                  <button
                    type="button"
                    onClick={() => { setShowForgotModal(false); resetFpModal(); }}
                    className="w-full rounded-xl bg-ga-green px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-ga-lime transition-colors"
                  >
                    Back to Sign In
                  </button>
                </div>
              ) : (
                <>
                  {/* Step indicator */}
                  <div className="flex items-center gap-2 mb-5">
                    {[1, 2, 3].map(s => (
                      <React.Fragment key={s}>
                        <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${fpStep >= s ? 'bg-ga-green text-slate-950' : 'bg-slate-700 text-slate-400'}`}>{s}</div>
                        {s < 3 && <div className={`h-0.5 flex-1 ${fpStep > s ? 'bg-ga-green' : 'bg-slate-700'}`} />}
                      </React.Fragment>
                    ))}
                  </div>

                  {fpStep === 1 && (
                    <>
                      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-ga-green/10">
                        <Phone className="h-6 w-6 text-ga-green" />
                      </div>
                      <h3 className="mb-1 text-lg font-semibold text-white">Forgot your password?</h3>
                      <p className="text-sm text-slate-400 mb-4">Enter your registered phone number to receive an OTP.</p>
                      <input
                        type="tel"
                        value={fpPhone}
                        onChange={e => setFpPhone(e.target.value)}
                        placeholder="10-digit phone number"
                        className="w-full rounded-xl border border-white/10 bg-slate-800 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-ga-green/50 focus:outline-none mb-3"
                      />
                      {fpError && <p className="text-xs text-red-400 mb-3">{fpError}</p>}
                      <button
                        type="button"
                        onClick={handleFpSendOtp}
                        disabled={fpLoading}
                        className="w-full rounded-xl bg-ga-green px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-ga-lime transition-colors disabled:opacity-60"
                      >
                        {fpLoading ? 'Sending…' : 'Send OTP'}
                      </button>
                    </>
                  )}

                  {fpStep === 2 && (
                    <>
                      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-ga-green/10">
                        <KeyRound className="h-6 w-6 text-ga-green" />
                      </div>
                      <h3 className="mb-1 text-lg font-semibold text-white">Enter OTP</h3>
                      <p className="text-sm text-slate-400 mb-4">A 6-digit code was sent to <span className="text-white">{fpPhone}</span>.</p>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={fpOtp}
                        onChange={e => setFpOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="6-digit OTP"
                        className="w-full rounded-xl border border-white/10 bg-slate-800 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-ga-green/50 focus:outline-none mb-3 tracking-widest text-center"
                      />
                      {fpError && <p className="text-xs text-red-400 mb-3">{fpError}</p>}
                      <button
                        type="button"
                        onClick={handleFpVerifyOtp}
                        className="w-full rounded-xl bg-ga-green px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-ga-lime transition-colors"
                      >
                        Verify OTP
                      </button>
                      <button
                        type="button"
                        onClick={() => { if (fpResendCooldown === 0) handleFpSendOtp(); }}
                        disabled={fpResendCooldown > 0 || fpLoading}
                        className="mt-3 w-full text-sm text-slate-400 hover:text-slate-200 disabled:opacity-50 transition-colors"
                      >
                        {fpResendCooldown > 0 ? `Resend OTP in ${fpResendCooldown}s` : 'Resend OTP'}
                      </button>
                    </>
                  )}

                  {fpStep === 3 && (
                    <>
                      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-ga-green/10">
                        <Lock className="h-6 w-6 text-ga-green" />
                      </div>
                      <h3 className="mb-1 text-lg font-semibold text-white">Set New Password</h3>
                      <p className="text-sm text-slate-400 mb-4">Choose a strong password (min 8 characters).</p>
                      <input
                        type="password"
                        value={fpPassword}
                        onChange={e => setFpPassword(e.target.value)}
                        placeholder="New password"
                        className="w-full rounded-xl border border-white/10 bg-slate-800 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-ga-green/50 focus:outline-none mb-3"
                      />
                      <input
                        type="password"
                        value={fpConfirm}
                        onChange={e => setFpConfirm(e.target.value)}
                        placeholder="Confirm password"
                        className="w-full rounded-xl border border-white/10 bg-slate-800 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-ga-green/50 focus:outline-none mb-3"
                      />
                      {fpError && <p className="text-xs text-red-400 mb-3">{fpError}</p>}
                      <button
                        type="button"
                        onClick={handleFpResetPassword}
                        disabled={fpLoading}
                        className="w-full rounded-xl bg-ga-green px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-ga-lime transition-colors disabled:opacity-60"
                      >
                        {fpLoading ? 'Resetting…' : 'Reset Password'}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        <GlowButton type="submit" disabled={loading} className="w-full py-3">
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-950" />
              <span>{method === 'phone' && !otpSent ? 'Sending code...' : 'Signing in...'}</span>
            </>
          ) : (
            <>
              <LogIn className="h-5 w-5" />
              <span>{method === 'phone' && !otpSent ? 'Send Code' : 'Sign In'}</span>
            </>
          )}
        </GlowButton>

        <div className="text-center">
          <button
            type="button"
            onClick={onMethodToggle}
            className="text-sm text-slate-400 hover:text-ga-lime transition-colors"
          >
            {method === 'phone'
              ? 'Sign in with username & password instead'
              : 'Sign in with phone number (OTP)'}
          </button>
        </div>
      </form>

      {method === 'phone' && !otpSent && (
        <p className="mt-6 text-center text-xs text-slate-500">
          New to Ground Aale? Just enter your phone number — we'll set you up.
        </p>
      )}
    </>
  );
}
